import { Injectable } from '@angular/core';
import { VaultApiService } from '@services/api/vault-api.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/* ===== DTOs de filtro (ajuste conforme seu back) ===== */
export interface DerivativeOperationDemandFilterDto {
  page?: number;
  pageSize?: number;
  companyId?: string;
  loanOperationId?: string;
  comexOperationId?: string;
  derivativeOperationDemandId?: string;
  status?: (string | null)[];
  operationFinancialStatus?: (string | null)[];
  /** Data base para cálculo de MTM (yyyy-MM-dd) */
  dataBase?: string;
  startDate?: string;
  endDate?: string;
  operationIds?: string[]; // Lista de IDs de operações específicas para filtrar
  /** Status negociais explicitamente autorizados no motor (ex.: Simulated, Quoted ou Hired). */
  operationStatuses?: string[];
  /**
   * Câmbio de cenário (substitui PTAX D-1 no motor de MtM).
   * Usado pela TIR/Loan Ops ao editar o FX da liquidação D-1.
   */
  spotOverride?: number | null;
}

/* ===== DTOs de resposta (camelCase) ===== */
export interface MTMCalculationResultDto {
  dataReferencia: string;
  taxaNegociada: number;
  taxaAtual?: number;
  notional?: number;
  diferencaTaxas?: number;
  mtmNdf?: number;
  mtmCallVendida?: number;
  netEstrutura?: number;
  ptaxD1?: number;
  diasCorridosAteVenc?: number;
  diasUteisAteVenc?: number;
  diInterpolado?: number;
  cupomCambialInterpolado?: number;
  volatilidadeAtm?: number;
  tempoVencimentoAnos?: number;
  forwardNdf?: number;
  strikeCall?: number;
  precoCallComprada?: number;
  precoCallVendida?: number;
  custoCallSpread?: number;
  ndfMaisCallVendida?: number;
  // Swap CCS/CCS-CAP: componentes do MtM separados (CCS puro × prêmio dos caps),
  // usados na sub-linha "CCS … · CAP …" da tabela de derivativos contratados.
  mtmSwap?: number;
  mtmCallsTotal?: number;
  tipoCalculo: string;
  erro?: string | null;
}

export interface MTMOperationDetailDto {
  idReferencia: string;
  operationId: string; // ID da operação de derivativo
  demandId: string; // ID da demanda de derivativo
  loanOperationId?: string | null; // ID da operação de empréstimo vinculada
  comexOperationId?: string | null; // ID da operação Comex vinculada
  origemOperacao: string;
  dataNegociacao: string; // ISO string
  dataInicio: string; // ISO string
  dataVencimento: string; // ISO string
  posicaoCliente: string;
  indexador: string;
  forward: number;
  spot: number;
  cap?: number | null;
  saldoMtm: number;
  notional: number;
  notionalXReferencia: number;
  taxaMercado?: number | null;
  valorMercado?: number | null;
  mtmDetails?: MTMCalculationResultDto | null;
  /** Compatibilidade com o JSON do backend para siglas: `SaldoMTM` serializa como `saldoMTM`. */
  saldoMTM?: number;
}

export interface MTMDerivativeTypeDataDto {
  operationsCount: number;
  totalMTM: number;
  operations: MTMOperationDetailDto[];
}

export interface MTMConsolidatedSummaryDto {
  totalOperations: number;
  totalMtm: number;
}

export interface DerivativeOperationDemandWithMtmDto {
  consolidatedSummary: MTMConsolidatedSummaryDto;
  ndfcap?: MTMDerivativeTypeDataDto | null; // mapeia C# NDFCAP
  ndfVanilla?: MTMDerivativeTypeDataDto | null; // mapeia C# NDFVanilla
  opcoes?: MTMDerivativeTypeDataDto | null; // mapeia C# Opcoes
  swap?: MTMDerivativeTypeDataDto | null; // mapeia C# SWAP
}

/* ===== HTTP Service ===== */
@Injectable({ providedIn: 'root' })
export class DerivativeMtmHttpService {
  // Ajuste a rota conforme o seu controller
  private readonly baseUrl = 'derivatives/demands';

  constructor(private api: VaultApiService) {}

  /**
   * POST {baseUrl}/filtered/with-mtm
   * Retorna MTM consolidado (objeto único) conforme filtros
   */
  getFilteredWithMtm(
    filter: DerivativeOperationDemandFilterDto
  ): Observable<DerivativeOperationDemandWithMtmDto | null> {
    // Agora usa POST com o filtro no body
    return this.api.post<DerivativeOperationDemandWithMtmDto>(
      `${this.baseUrl}/filtered/with-mtm`,
      filter
    );
  }

  /**
   * Lista as operações NDF/derivativos contratados vinculados a uma operação de crédito.
   * GET operations/ndf/filtered/summary?loanOperationId=... — usado para obter os IDs das operações
   * de uma dívida (o `with-mtm` filtrado por loanOperationId retorna demandas, não as operações).
   */
  getNdfOperationsByLoan(loanOperationId: string): Observable<NdfOperationSummaryItem[]> {
    return this.api
      .get<NdfOperationSummaryPage>(
        `operations/ndf/filtered/summary?loanOperationId=${encodeURIComponent(loanOperationId)}&page=1&pageSize=500`
      )
      .pipe(map(page => page?.data ?? []));
  }
}

/** Item da listagem de operações NDF por dívida (`operations/ndf/filtered/summary`). */
export interface NdfOperationSummaryItem {
  id: string;
  sequentialId?: string;
  operationStrategyType?: string;
  operationStatus?: string;
  financialStatus?: string;
  notionalAmount?: number;
  forwardRate?: number;
  capValue?: number | null;
  dueAt?: string;
  // Campos retornados pelo mesmo endpoint e usados na reprecificação/colunas da tela de derivativos
  // contratados (paridade com o `Calculadora_Derivativos`).
  strikeBuy?: number | null;
  strikeSell?: number | null;
  premium?: number | null;
  derivativeOperationDemandId?: string | null;
  referenceExchangeRate?: number | null;
  settlementRate?: number | null;
  activeFixedRatePerYear?: number | null;
  passiveFixedRatePerYear?: number | null;
  activeIndexer?: string | null;
  passiveIndexer?: string | null;
  netAmountRounded?: number | null;
  totalAmountRounded?: number | null;
  reference?: string | null;
  totalAnnualCost?: number | null;
}

/** Página retornada por `operations/ndf/filtered/summary`. */
interface NdfOperationSummaryPage {
  data: NdfOperationSummaryItem[];
}
